import { useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { KeyValue, Pill, FormGrid, Field, TextInput, TextArea } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { dateLabel } from '../../lib/format';
import type { Supplier } from '../../lib/types';

// 供应商详情（只读）：主数据以编码/名称/税号/联系方式为准，采购入库归采购订单模块维护。
function SupplierDetail({ row }: { row: Supplier }) {
  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{row.code}</span>
        <h3>{row.legal_name}</h3>
        <Pill tone={row.active ? 'ok' : 'neutral'} label={row.active ? '启用' : '停用'} />
      </div>
      <div className="kv-grid">
        <KeyValue k="税号 / CUIT">{row.tax_id || '—'}</KeyValue>
        <KeyValue k="邮箱">{row.email || '—'}</KeyValue>
        <KeyValue k="电话">{row.phone || '—'}</KeyValue>
        <KeyValue k="地址">{row.address || '—'}</KeyValue>
        <KeyValue k="ERPNext 编号">{row.legacy_erpnext_id || '—'}</KeyValue>
        <KeyValue k="建档日期">{dateLabel(row.created_at)}</KeyValue>
      </div>
    </div>
  );
}

function NewSupplierForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [f, setF] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((d) => ({ ...d, [k]: v }));

  const submit = async () => {
    setErr(null);
    if (!f.code?.trim()) { setErr('供应商编码为必填'); return; }
    if (!f.legal_name?.trim()) { setErr('供应商名称为必填'); return; }
    setBusy(true);
    try {
      await api.post('/api/suppliers', {
        code: f.code.trim(),
        legal_name: f.legal_name.trim(),
        tax_id: f.tax_id || null,
        email: f.email || null,
        phone: f.phone || null,
        address: f.address || null,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '创建失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head">
        <div>
          <h2>新建供应商</h2>
          <div className="desc">写入核心服务 /api/suppliers · 采购来源主数据（编码唯一）。国内供应商多为 ARS 结算，进口供应商以外币报价；供应商单据号在采购收货时留痕</div>
        </div>
      </div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="供应商编码" required><TextInput value={f.code ?? ''} onChange={(e) => set('code', e.target.value)} placeholder="如 SUP-001" /></Field>
          <Field label="名称 / 公司名" required><TextInput value={f.legal_name ?? ''} onChange={(e) => set('legal_name', e.target.value)} /></Field>
          <Field label="税号 / CUIT"><TextInput value={f.tax_id ?? ''} onChange={(e) => set('tax_id', e.target.value)} placeholder="30-00000000-0" /></Field>
          <Field label="邮箱"><TextInput value={f.email ?? ''} onChange={(e) => set('email', e.target.value)} /></Field>
          <Field label="电话"><TextInput value={f.phone ?? ''} onChange={(e) => set('phone', e.target.value)} /></Field>
          <Field label="地址" span><TextArea value={f.address ?? ''} onChange={(e) => set('address', e.target.value)} /></Field>
        </FormGrid>
        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : '创建供应商'}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

export function SuppliersPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const canWrite = can(user, 'suppliers', 'create');
  const bump = () => setReloadSignal((n) => n + 1);

  const columns: Column<Supplier>[] = [
    { header: '编码', render: (s) => <span className="num strong">{s.code}</span> },
    { header: '名称', render: (s) => s.legal_name },
    { header: '税号 / CUIT', render: (s) => <span className="num muted">{s.tax_id || '—'}</span> },
    { header: '邮箱', render: (s) => <span className="muted">{s.email || '—'}</span> },
    { header: '电话', render: (s) => <span className="muted">{s.phone || '—'}</span> },
    { header: '状态', render: (s) => <Pill tone={s.active ? 'ok' : 'neutral'} label={s.active ? '启用' : '停用'} /> },
  ];

  const rowActions = (row: Supplier) => (
    <RecordActions type="suppliers" row={row} fields={RECORD_FIELDS.suppliers} label={row.legal_name} onDone={bump} />
  );

  return (
    <>
      {creating ? (
        <NewSupplierForm onCancel={() => setCreating(false)} onCreated={() => { setCreating(false); bump(); }} />
      ) : null}
      <ListPage<Supplier>
        title="供应商"
        desc="采购来源主数据 · /api/suppliers。展开查看联系方式与 ERPNext 来源编号；采购下单与收货入库在「采购订单」模块完成"
        endpoint="/api/suppliers"
        columns={columns}
        rowId={(s) => s.id}
        showYear={false}
        searchPlaceholder="搜索编码 / 名称 / 税号…"
        reloadSignal={reloadSignal}
        rowActions={rowActions}
        actions={canWrite ? (
          <button className="btn primary" onClick={() => setCreating((v) => !v)}>{creating ? '收起新建' : '新建供应商'}</button>
        ) : undefined}
        renderDetail={(row) => <SupplierDetail row={row} />}
      />
    </>
  );
}
